import {
	ActionBar,
	Button,
	Checkbox,
	ChevDownIcon,
	IssueIcon,
	Label,
	Modal,
	Select,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../../components/GenericTable'
import { useFeatureFlag } from '../../../hooks/useFeatureFlag'
import { useToast } from '../../../utils/ToastContext'
import { RemoveParticipantDialog } from '../components/RemoveParticipantDialog'
import { useCycleStore } from '../store'
import {
	createCycleParticipantsStore,
	type Participant,
	type SearchParticipantsQuery,
	type SearchUsersQuery,
	type User,
} from '../stores/cycleParticipantsStore'

// Custom multi-select with checkboxes component
function MultiSelectWithCheckboxes({
	options,
	value,
	onChange,
	placeholder = 'Select...',
}: {
	options: { value: string; label: string }[]
	value: string[]
	onChange: (value: string[]) => void
	placeholder?: string
}) {
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleToggle = (optionValue: string) => {
		if (value.includes(optionValue)) {
			onChange(value.filter((v) => v !== optionValue))
		} else {
			onChange([...value, optionValue])
		}
	}

	const displayText = value.length > 0 ? `${value.length} selected` : placeholder

	return (
		<div className='relative w-full' ref={containerRef}>
			<div
				role='combobox'
				tabIndex={0}
				aria-expanded={isOpen}
				className='flex items-center justify-between cursor-pointer min-h-[44px] px-3 py-2 border border-neutral-300 rounded-sm bg-white hover:border-neutral-400'
				onClick={() => setIsOpen(!isOpen)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') setIsOpen(!isOpen)
				}}>
				<span className='truncate flex-1 mr-2 text-sm'>{displayText}</span>
				<ChevDownIcon size={16} className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
			</div>

			{isOpen && (
				<div className='absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-sm shadow-lg max-h-60 overflow-y-auto'>
					{options.map((option) => (
						<div
							role='option'
							tabIndex={0}
							aria-selected={value.includes(option.value)}
							key={option.value}
							className='px-3 py-2 hover:bg-neutral-50 cursor-pointer flex items-center gap-2'
							onClick={() => handleToggle(option.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') handleToggle(option.value)
							}}>
							<Checkbox checked={value.includes(option.value)} onChange={() => handleToggle(option.value)} />
							<span className='text-sm'>{option.label}</span>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

interface Props {
	useStore?: typeof createCycleParticipantsStore
}

export function CycleParticipantsTab({ useStore = createCycleParticipantsStore }: Props) {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const {
		participants,
		selectedParticipants,
		users,
		addParticipantsQuery,
		customFields,
		selectedCustomField,
		customFieldsFilters,
		forms,
		error,
		loading,
		preDeleteCheckResult,
		preDeleteCheckLoading,
		deleteLoading,
		initialize,
		setSearchParticipantsQuery,
		setSelectedParticipants,
		runPreDeleteCheck,
		deleteSelectedParticipants,
		setSelectedUsers,
		addParticipants,
		setSearchUsersQuery,
		setSelectedCustomField,
		setSelectedCustomFieldSelectedValues,
		addCustomFieldFilter,
		setFormAssignment,
		searchNextForms,
		removeCustomFieldFilter,
		clearError,
	} = useStore()
	const { cycle } = useCycleStore()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
	const { showToast } = useToast()
	// Whole removal flow is gated; the Remove action bar is hidden when off so the
	// admin can't even initiate a delete. The API also refuses up-front as a
	// belt-and-suspenders for direct tRPC callers.
	const { isEnabled: removalEnabled } = useFeatureFlag('participant-soft-delete')

	const canDeleteSelected = selectedParticipants.length > 0
	const mountedRef = useRef(false)

	useEffect(() => {
		if (!mountedRef.current) {
			mountedRef.current = true

			if (cycleId && organizationId) {
				initialize({
					cycleId,
					organizationId,
				})
			}
		}
	}, [organizationId, cycleId, initialize])

	useEffect(() => {
		if (error) {
			showToast(error, 'error')
			clearError()
		}
	}, [error, clearError, showToast])

	const handleOpenModal = () => {
		setIsModalOpen(true)
	}

	const handleOpenRemoveDialog = async () => {
		const result = await runPreDeleteCheck()
		if (result) {
			setIsRemoveDialogOpen(true)
		}
	}

	const handleCloseRemoveDialog = () => {
		setIsRemoveDialogOpen(false)
	}

	const handleConfirmRemove = async (mode: 'display' | 'hide') => {
		const succeeded = await deleteSelectedParticipants(mode)
		if (!succeeded) return
		setIsRemoveDialogOpen(false)
		showToast('Participants removed successfully')
	}

	const selectedParticipantNames =
		participants?.data?.filter((p) => selectedParticipants.includes(p.id)).map((p) => `${p.firstName} ${p.lastName}`) ??
		[]

	const hasData = (preDeleteCheckResult?.participantsWithData?.length ?? 0) > 0

	const addButtonLabel = addParticipantsQuery?.selectAllMode
		? 'Add participants (all)'
		: addParticipantsQuery?.include?.length > 0
			? `Add participants (${addParticipantsQuery.include.length})`
			: 'Add participants'

	return loading ? (
		<div>
			<SpinnerWithBackground />
		</div>
	) : (
		<div className='mt-6'>
			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<Participant, SearchParticipantsQuery>
					data={participants}
					selectable
					onSelectionChange={setSelectedParticipants}
					onSearchChange={setSearchParticipantsQuery}
					columns={[
						{
							field: 'firstName',
							label: 'Name',
							colSpan: 2,
							render: (participant) => `${participant.firstName} ${participant.lastName}`,
						},
						{
							field: 'externalId',
							label: 'ID',
							colSpan: 1,
						},
						{
							field: 'email',
							label: 'Email',
							colSpan: 2,
						},
						{
							field: 'active',
							label: 'Status',
							render: (participant) => (
								<div className='flex'>
									<Label className='self-start' color={participant.active ? 'green' : 'default'}>
										{participant.active ? 'Active' : 'Inactive'}
									</Label>
								</div>
							),
						},
					]}
					showPagination
					actionButtons={[
						{
							children: 'Add participant',
							onClick: handleOpenModal,
						},
					]}
					emptyState={{
						title: 'No cycle participants added yet',
						description: 'Get started by adding cycle participants',
						actionLabel: 'Add cycle participant',
						icon: IssueIcon,
						onAction: handleOpenModal,
					}}
				/>
			</div>

			<Modal
				isOpen={isModalOpen}
				fullContent
				onClose={() => setIsModalOpen(false)}
				fullScreen
				title='Add Cycle Participants'>
				<div className='flex flex-col h-full'>
					<div className='p-4 border-b border-neutral-100 flex justify-between flex-shrink-0'>
						<div>
							<Text variant='small' weight='semibold' className='mb-3'>
								Filter by Custom Fields
							</Text>
							<div className='flex gap-3 items-end'>
								<div className='w-60'>
									<span className='block text-xs font-medium text-neutral-700 mb-1'>Select Field</span>
									<Select
										value={selectedCustomField?.name}
										options={
											customFields?.map((field) => ({
												label: field,
												value: field,
											})) ?? []
										}
										onChange={setSelectedCustomField}
										placeholder='Select a custom field'
									/>
								</div>
								{selectedCustomField && (
									<>
										<div className='w-60'>
											<span className='block text-xs font-medium text-neutral-700 mb-1'>Select Values</span>
											<MultiSelectWithCheckboxes
												value={selectedCustomField.selectedValues}
												options={
													selectedCustomField.possibleValues?.map((value) => ({
														label: value,
														value: value,
													})) ?? []
												}
												onChange={setSelectedCustomFieldSelectedValues}
												placeholder='Select multiple values'
											/>
										</div>
										{selectedCustomField.possibleValues.length > 0 && (
											<Button variant='primary' size='small' onClick={addCustomFieldFilter}>
												Add Filter
											</Button>
										)}
									</>
								)}
							</div>
							{customFieldsFilters && Object.keys(customFieldsFilters).length > 0 && (
								<div className='mt-3 flex flex-col'>
									<Text variant='xsmall' weight='semibold' className='mb-2'>
										Active Filters:
									</Text>
									<div className='flex flex-wrap gap-2 pl-5'>
										{Object.entries(customFieldsFilters).map(([field, values]) => (
											<div key={field} className='flex items-center gap-1'>
												<Text variant='xsmall' weight='semibold'>
													{field}:
												</Text>
												{values.map((value) => (
													<Label key={value} color='green'>
														{value}
													</Label>
												))}
												<button
													type='button'
													onClick={() => removeCustomFieldFilter(field)}
													className='ml-1 text-red-500 hover:text-red-700'>
													×
												</button>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
						<div className='flex items-end flex-col w-60'>
							<Text variant='small' weight='semibold' className='mb-3'>
								Form Assignment
							</Text>
							<span className='block text-xs font-medium text-neutral-700 mb-1'>Select Form</span>
							<Select
								value={addParticipantsQuery?.formId ?? ''}
								options={
									forms?.data?.map((form) => ({
										label: form.name,
										value: form.id,
									})) ?? []
								}
								onChange={setFormAssignment}
								hasMore={forms?.data?.length < forms?.meta?.total}
								onMenuScrollToBottom={searchNextForms}
								placeholder='Select a form'
							/>
						</div>
					</div>
					<GenericTable<User, SearchUsersQuery>
						data={users}
						isInternal
						selectable
						onSelectionChange={setSelectedUsers}
						onSearchChange={setSearchUsersQuery}
						columns={[
							{
								field: 'firstName',
								label: 'Name',
								colSpan: 2,
								render: (user) => `${user.firstName} ${user.lastName}`,
							},
							{
								field: 'externalId',
								label: 'ID',
								colSpan: 1,
							},
							{
								field: 'email',
								label: 'Email',
								colSpan: 2,
							},
						]}
						selectAllAcrossPages
					/>
					<ActionBar
						variant='modal'
						className='left-0 flex justify-end flex-shrink-0'
						rightActions={[
							{
								label: 'Cancel',
								variant: 'secondary',
								onClick: () => setIsModalOpen(false),
							},
							{
								label: addButtonLabel,
								variant: 'primary',
								onClick: () => {
									addParticipants()
									setIsModalOpen(false)
								},
								disabled: !addParticipantsQuery?.selectAllMode && !addParticipantsQuery?.include?.length,
							},
						]}
					/>
				</div>
			</Modal>

			{selectedParticipants.length > 0 && removalEnabled && (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedParticipants([]),
						},
						{
							label: `Remove (${selectedParticipants.length})`,
							variant: 'destructive',
							onClick: handleOpenRemoveDialog,
							disabled: !canDeleteSelected || preDeleteCheckLoading,
						},
					]}
				/>
			)}

			<RemoveParticipantDialog
				isOpen={isRemoveDialogOpen}
				onClose={handleCloseRemoveDialog}
				onConfirm={handleConfirmRemove}
				participantNames={selectedParticipantNames}
				hasData={hasData}
				cycleStatus={(cycle?.status as 'draft' | 'in-progress' | 'ready-for-launch' | 'completed') ?? 'draft'}
				cycleName={cycle?.name ?? ''}
				isLoading={deleteLoading}
				softDeleteEnabled={preDeleteCheckResult?.softDeleteEnabled ?? false}
			/>
		</div>
	)
}
